// src/features/scheduleGenerator/services/scheduleGeneratorService.ts
import { getFunctions, httpsCallable, HttpsCallableOptions } from "firebase/functions";
import { TimeBox } from "../../../types/timeBox";
import { Event } from "../../../types/calendar-event";
import { ScheduleItem } from "../../../types/scheduleItem";
import { logDev, logError } from "../../../utils/logger";
import { Option } from "../../../components/common/ChoiceQuestion";
import { EventCategory } from "../../../types/calendar-event";

// ============================================================
// Day Draft V3 Types
// ============================================================

export type ToneOption = Option;

export interface ToneOptionsResult {
  summary: string;
  options: ToneOption[];
}

export interface SkeletonTimeBlock {
  title: string;
  startTime: string;  // HH:MM（用戶時區）
  endTime: string;    // HH:MM（用戶時區）
  category: 'focus' | 'routine' | 'life' | 'rest';
}

export interface SkeletonEvent {
  title: string;
  startTime: string;   // HH:MM（用戶時區）
  endTime: string;     // HH:MM（用戶時區）
  category: EventCategory;
}

export interface SkeletonAlldayEvent {
  title: string;
  category: EventCategory;
}

export interface SkeletonResult {
  timeBlocks: SkeletonTimeBlock[];
  events: SkeletonEvent[];
  instructionSummary?: string;
}

export interface TaskRecommendation {
  taskId: string;
  reason: string;
}

export interface RecommendTasksResult {
  recommendations: TaskRecommendation[];
  tasks: Pick<TimeBox, 'id' | 'title'>[];
}

export interface TaskAssignmentTimeBox {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  category: string;
  taskIds: string[];
}

export interface AssignTasksResult {
  timeBoxes: TaskAssignmentTimeBox[];
}

// ============================================================
// Day Draft V3 API Calls
// ============================================================

export const generateToneOptions = async (date: string): Promise<ToneOptionsResult> => {
  try {
    const func = httpsCallable(getFunctions(), 'generateToneOptions');
    const result = await func({ date });
    return result.data as ToneOptionsResult;
  } catch (error) {
    logError('ScheduleGeneratorApi', 'generateToneOptions 失敗:', error);
    throw error;
  }
};

export const generateSkeleton = async (
  date: string,
  intention: string,
  currentTimeBlocks?: SkeletonTimeBlock[],
  instruction?: string,
  instructionSummary?: string,
  toneSummary?: string,
): Promise<SkeletonResult> => {
  try {
    const func = httpsCallable(getFunctions(), 'generateSkeleton');
    const result = await func({ date, intention, currentTimeBlocks, instruction, instructionSummary, toneSummary });
    return result.data as SkeletonResult;
  } catch (error) {
    logError('ScheduleGeneratorApi', 'generateSkeleton 失敗:', error);
    throw error;
  }
};

export const recommendTasks = async (
  date: string,
  intention: string,
  skeletonTimeBoxes: SkeletonTimeBlock[]
): Promise<RecommendTasksResult> => {
  try {
    const func = httpsCallable(getFunctions(), 'recommendTasks');
    const result = await func({ date, intention, skeletonTimeBoxes });
    return result.data as RecommendTasksResult;
  } catch (error) {
    logError('ScheduleGeneratorApi', 'recommendTasks 失敗:', error);
    throw error;
  }
};

export const assignTasksToTimeBoxes = async (
  date: string,
  selectedTaskIds: string[],
  timeBoxes: TaskAssignmentTimeBox[],
  taskDetails: Array<{ id: string; title: string; category?: string; estimatedTime?: number }>
): Promise<AssignTasksResult> => {
  try {
    const func = httpsCallable(getFunctions(), 'assignTasksToTimeBoxes');
    const result = await func({ date, selectedTaskIds, timeBoxes, taskDetails });
    return result.data as AssignTasksResult;
  } catch (error) {
    logError('ScheduleGeneratorApi', 'assignTasksToTimeBoxes 失敗:', error);
    throw error;
  }
};

// 生成一日計畫
export const generateDailyPlan = async (
  date: string,
  latestNote?: string,
  lastSummary?: string,
  signal?: AbortSignal,
  existingTimeBoxes?: TimeBox[]
) => {
  try {
    const functions = getFunctions();

    const options: HttpsCallableOptions = {};
    if (signal && signal.aborted) {
      throw new DOMException('Request aborted', 'AbortError');
    }

    const generateDailyPlanFunc = httpsCallable(functions, 'generateDailyPlan', options);

    const params: {
      date: string;
      latestNote?: string;
      lastSummary?: string;
      existingTimeBoxes?: TimeBox[];
    } = { date };

    if (latestNote) params.latestNote = latestNote;
    if (lastSummary) params.lastSummary = lastSummary;
    if (existingTimeBoxes) params.existingTimeBoxes = existingTimeBoxes;

    const result = await generateDailyPlanFunc(params);
    return result.data as {
      date: string;
      timeBoxes: TimeBox[];       // 保留向後相容
      events?: Event[];           // 新增 Event 陣列
      scheduleItems?: ScheduleItem[]; // 新增完整排程
      summary?: string;
    };
  } catch (error) {
    // 檢查是否是取消錯誤
    if (signal?.aborted) {
      logDev('ScheduleGeneratorApi', 'AI 計劃生成請求已取消');
      // 重新拋出取消錯誤，以便上層處理
      throw new DOMException('Request aborted', 'AbortError');
    }

    logError('ScheduleGeneratorApi', '生成計畫失敗:', error);
    throw error;
  }
};

// 同步排程項目（統一處理 TimeBox 和 Event）
export const syncDailySchedule = async (
  date: string,
  scheduleItems: ScheduleItem[],
  preserveCategories: string[] = [],
  planningNotes?: string[] // 規劃過程中的 notes
) => {
  try {
    const functions = getFunctions();
    const syncDailyScheduleFunc = httpsCallable(
      functions,
      'syncDailySchedule'
    );

    const params = {
      date, // YYYY-MM-DD 格式
      scheduleItems, // 包含完整的排程項目列表（TimeBox 和 Event）
      preserveCategories, // 可選，需要保留的時間箱類別
      planningNotes // 可選，規劃過程中的 notes
    };

    const result = await syncDailyScheduleFunc(params);

    return result.data as {
      success: boolean;
      message: string;
      timeBoxes: {
        created: { count: number; ids: string[]; };
        updated: { count: number; ids: string[]; };
        deleted: { count: number; ids: string[]; };
      };
      events: {
        created: { count: number; ids: string[]; };
        updated: { count: number; ids: string[]; };
        deleted: { count: number; ids: string[]; };
      };
      // 向後兼容的統計格式
      created: { count: number; ids: string[]; };
      updated: { count: number; ids: string[]; };
      deleted: { count: number; ids: string[]; };
      date: string;
    };
  } catch (error) {
    logError('ScheduleGeneratorApi', '同步排程項目失敗:', error);
    throw error;
  }
};
